/**
 * Gazette blog — AJAX category tabs, search and lazy loaded pagination.
 *
 * Grid HTML is fetched from the alternate templates `blog.gazette-ajax` /
 * `search.gazette-ajax` (`?view=gazette-ajax`), which render
 * `snippets/gazette-article-grid` only, so the markup is identical to the one
 * printed server side.
 */
(function () {
  'use strict';

  var SEARCH_DEBOUNCE = 400;

  function Gazette(root) {
    this.root = root;
    this.results = root.querySelector('[data-gazette-results]');
    this.sentinel = root.querySelector('[data-gazette-sentinel]');
    this.loader = root.querySelector('[data-gazette-loader]');
    this.form = root.querySelector('[data-gazette-search-form]');
    this.input = root.querySelector('[data-gazette-search-input]');
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-gazette-tab]'));

    this.blogUrl = (root.getAttribute('data-blog-url') || '').replace(/\/$/, '');
    this.searchUrl = root.getAttribute('data-search-url') || '/search';
    this.tag = root.getAttribute('data-active-tag') || '';
    this.terms = root.getAttribute('data-terms') || '';
    this.tagHandle = this.handleForTag(this.tag);

    this.page = 1;
    this.hasNext = false;
    this.loading = false;
    this.requestId = 0;

    this.readMeta();
    this.bindEvents();
    this.observe();

    root.classList.add('gazette-blog--enhanced');

    this.restoreFromUrl();
  }

  /* Filters live in the URL (?tag=&q=) so a filtered view stays shareable even
     when the section sits on a page template, where Liquid cannot read them. */
  Gazette.prototype.restoreFromUrl = function () {
    var params = new URLSearchParams(window.location.search);
    var tagHandle = params.get('tag') || '';
    var terms = params.get('q') || '';
    var changed = false;

    if (tagHandle && tagHandle !== this.tagHandle) {
      for (var i = 0; i < this.tabs.length; i++) {
        if (this.tabs[i].getAttribute('data-tag-handle') === tagHandle) {
          this.activateTab(this.tabs[i]);
          this.tag = this.tabs[i].getAttribute('data-tag') || '';
          this.tagHandle = tagHandle;
          changed = true;
          break;
        }
      }
    }

    if (terms && terms !== this.terms) {
      this.terms = terms;
      if (this.input) this.input.value = terms;
      changed = true;
    }

    if (changed) this.load(1, false);
  };

  Gazette.prototype.handleForTag = function (tag) {
    for (var i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].getAttribute('data-tag') === tag) {
        return this.tabs[i].getAttribute('data-tag-handle') || '';
      }
    }
    return '';
  };

  Gazette.prototype.readMeta = function () {
    var meta = this.results.querySelector('[data-gazette-meta]');
    this.hasNext = !!meta && meta.getAttribute('data-has-next') === 'true';
    this.page = meta ? parseInt(meta.getAttribute('data-current-page'), 10) || 1 : 1;
    this.toggleSentinel();
  };

  Gazette.prototype.toggleSentinel = function () {
    if (!this.sentinel) return;
    this.sentinel.hidden = !this.hasNext;
  };

  Gazette.prototype.bindEvents = function () {
    var self = this;

    this.tabs.forEach(function (tab) {
      tab.addEventListener('click', function (event) {
        event.preventDefault();
        self.setTag(tab);
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', function (event) {
        event.preventDefault();
        self.setTerms(self.input ? self.input.value : '');
      });
    }

    if (this.input) {
      var timer = null;
      this.input.addEventListener('input', function () {
        clearTimeout(timer);
        var value = self.input.value;
        timer = setTimeout(function () {
          self.setTerms(value);
        }, SEARCH_DEBOUNCE);
      });
    }
  };

  Gazette.prototype.activateTab = function (tab) {
    this.tabs.forEach(function (item) {
      var active = item === tab;
      item.classList.toggle('is-active', active);
      if (active) {
        item.setAttribute('aria-current', 'page');
      } else {
        item.removeAttribute('aria-current');
      }
    });
  };

  Gazette.prototype.setTag = function (tab) {
    var tag = tab.getAttribute('data-tag') || '';
    if (tag === this.tag) return;

    this.tag = tag;
    this.tagHandle = tab.getAttribute('data-tag-handle') || '';
    this.activateTab(tab);

    this.load(1, false);
  };

  Gazette.prototype.setTerms = function (value) {
    var terms = (value || '').trim();
    if (terms === this.terms) return;
    this.terms = terms;
    this.load(1, false);
  };

  Gazette.prototype.buildUrl = function (page) {
    var params = ['view=gazette-ajax'];
    if (page > 1) params.push('page=' + page);

    var url;
    if (this.terms) {
      var query = this.terms;
      if (this.tag) query += ' tag:"' + this.tag + '"';
      url = this.searchUrl;
      params.push('type=article');
      params.push('options%5Bprefix%5D=last');
      params.push('q=' + encodeURIComponent(query));
    } else {
      url = this.blogUrl + (this.tagHandle ? '/tagged/' + this.tagHandle : '');
    }

    return url + '?' + params.join('&');
  };

  /* Current filter state on the current path, keeping any unrelated params. */
  Gazette.prototype.publicUrl = function () {
    var params = new URLSearchParams(window.location.search);

    params.delete('page');
    if (this.tagHandle) {
      params.set('tag', this.tagHandle);
    } else {
      params.delete('tag');
    }
    if (this.terms) {
      params.set('q', this.terms);
    } else {
      params.delete('q');
    }

    var query = params.toString();
    return window.location.pathname + (query ? '?' + query : '');
  };

  Gazette.prototype.load = function (page, append) {
    var self = this;
    var id = ++this.requestId;

    this.loading = true;
    this.setBusy(true, append);

    fetch(this.buildUrl(page), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (response) {
        /* Shopify answers 404 for a tag no article carries — that is an empty
           result, not a failure. */
        if (response.status === 404) return '';
        if (!response.ok) throw new Error('Gazette request failed: ' + response.status);
        return response.text();
      })
      .then(function (html) {
        if (id !== self.requestId) return;
        self.apply(html, append);
        if (!append && window.history && window.history.replaceState) {
          window.history.replaceState({}, '', self.publicUrl());
        }
      })
      .catch(function (error) {
        if (id !== self.requestId) return;
        console.error(error);
        self.hasNext = false;
        self.toggleSentinel();
      })
      .then(function () {
        if (id !== self.requestId) return;
        self.loading = false;
        self.setBusy(false, append);
        /* The sentinel can stay inside the root margin after appending — the
           observer only reports changes, so re-arm it to get a fresh callback. */
        self.rearmObserver();
      });
  };

  Gazette.prototype.apply = function (html, append) {
    if (!html) {
      if (!append) this.results.innerHTML = this.emptyMarkup();
      this.hasNext = false;
      this.toggleSentinel();
      return;
    }

    var doc = new DOMParser().parseFromString(html, 'text/html');
    var incomingGrid = doc.querySelector('[data-gazette-grid]');

    if (!append || !incomingGrid) {
      this.results.innerHTML = doc.body.innerHTML;
    } else {
      var grid = this.results.querySelector('[data-gazette-grid]');
      var cards = Array.prototype.slice.call(incomingGrid.children);

      if (grid) {
        cards.forEach(function (card) {
          grid.appendChild(card);
        });
      }

      var meta = this.results.querySelector('[data-gazette-meta]');
      var incomingMeta = doc.querySelector('[data-gazette-meta]');
      if (meta && incomingMeta) meta.parentNode.replaceChild(incomingMeta, meta);

      var pagination = this.results.querySelector('.gz-pagination');
      var incomingPagination = doc.querySelector('.gz-pagination');
      if (pagination) {
        if (incomingPagination) {
          pagination.parentNode.replaceChild(incomingPagination, pagination);
        } else {
          pagination.parentNode.removeChild(pagination);
        }
      }
    }

    this.filterByTag();
    this.labelCategories();
    this.readMeta();
  };

  /* The AJAX views render without the section settings, so cards arrive without a
     category label — derive it from the tabs, which hold the same list. */
  Gazette.prototype.labelCategories = function () {
    var categories = this.tabs
      .filter(function (tab) {
        return tab.getAttribute('data-tag-handle');
      })
      .map(function (tab) {
        return { handle: tab.getAttribute('data-tag-handle'), label: tab.getAttribute('data-tag') };
      });

    if (!categories.length) return;

    Array.prototype.forEach.call(this.results.querySelectorAll('.gz-card[data-tags]'), function (card) {
      var meta = card.querySelector('.gz-card__meta');
      if (!meta || card.querySelector('.gz-card__cat')) return;

      var tags = '|' + card.getAttribute('data-tags');
      for (var i = 0; i < categories.length; i++) {
        if (tags.indexOf('|' + categories[i].handle + '|') === -1) continue;

        var label = document.createElement('span');
        label.className = 'gz-card__cat';
        label.textContent = categories[i].label;

        var separator = document.createElement('span');
        separator.className = 'gz-card__sep';
        separator.setAttribute('aria-hidden', 'true');

        meta.insertBefore(separator, meta.firstChild);
        meta.insertBefore(label, meta.firstChild);
        return;
      }
    });
  };

  /* A search request cannot be restricted to a tag server side (Shopify's `tag:`
     field matches loosely), so drop the cards that do not carry the category. */
  Gazette.prototype.filterByTag = function () {
    if (!this.terms || !this.tagHandle) return;

    var needle = '|' + this.tagHandle + '|';
    var grid = this.results.querySelector('[data-gazette-grid]');
    if (!grid) return;

    Array.prototype.forEach.call(grid.querySelectorAll('.gz-card[data-tags]'), function (card) {
      if (('|' + card.getAttribute('data-tags')).indexOf(needle) === -1) {
        card.parentNode.removeChild(card);
      }
    });

    var empty = this.results.querySelector('.gz-empty');
    if (!grid.children.length && !empty) {
      grid.insertAdjacentHTML('afterend', this.emptyMarkup(true));
    } else if (grid.children.length && empty) {
      empty.parentNode.removeChild(empty);
    }
  };

  Gazette.prototype.emptyMarkup = function (messageOnly) {
    var text = this.root.getAttribute('data-empty-text') || 'No articles found.';
    var node = document.createElement('p');
    node.className = 'gz-empty';
    node.textContent = text;
    if (messageOnly) return node.outerHTML;
    return '<div class="gz-grid" data-gazette-grid></div>' + node.outerHTML;
  };

  Gazette.prototype.setBusy = function (busy, append) {
    this.results.setAttribute('aria-busy', busy ? 'true' : 'false');
    this.results.classList.toggle('is-loading', busy && !append);
    if (this.loader) this.loader.hidden = !(busy && append);
  };

  Gazette.prototype.observe = function () {
    if (!this.sentinel || typeof IntersectionObserver === 'undefined') return;

    var self = this;
    this.root.classList.add('gazette-blog--infinite');

    this.observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting || self.loading || !self.hasNext) return;
          self.load(self.page + 1, true);
        });
      },
      { rootMargin: '400px 0px' }
    );

    this.observer.observe(this.sentinel);
  };

  /* The observer only reports *changes*: right after appending, the sentinel is
     either still inside the root margin or already scrolled past, and in both
     cases no new callback fires. Re-arm it and check the position by hand. */
  Gazette.prototype.rearmObserver = function () {
    if (!this.observer || !this.sentinel || !this.hasNext) return;

    this.observer.unobserve(this.sentinel);
    this.observer.observe(this.sentinel);

    var self = this;
    window.requestAnimationFrame(function () {
      if (self.loading || !self.hasNext) return;
      var rect = self.sentinel.getBoundingClientRect();
      if (rect.top < window.innerHeight + 400) self.load(self.page + 1, true);
    });
  };

  function init(context) {
    var roots = (context || document).querySelectorAll('[data-gazette]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.gazette) return;
      root.gazette = new Gazette(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
